
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@univdiam/ui/components/button";
import { ProductMasterDataTable } from "@univdiam/ui/customComponents/ProductMasterList/data-table";
import { ProductMasterColumns } from "@univdiam/ui/customComponents/ProductMasterList/columns";
import { EditProductMasterForm } from "@univdiam/ui/customComponents/ProductMasterList/EditProductMasterForm";
import { ProductDetailModal } from "@univdiam/ui/customComponents/ProductMasterList/ProductDetailModal";
import axios from "axios";

const API_BASE_URL = import.meta.env.VITE_SERVER_URL;

export default function ProductMasterPage() {
  const [groupData, setGroupData] = useState(null)
  const [subGroupData, setSubGroupData] = useState(null)
  const [shapeSizeData, setShapeSizeData] = useState(null)
  const [subSubGroupData, setSubSubGroupData] = useState(null)
  const [data, setData] = useState([]);
  const [editProductMasterData, setEditProductMasterData] = useState(null);
  const [showModalData, setShowModalData] = useState(null);
  const [initialData, setInitialData] = useState(null)
  const navigate = useNavigate();

  const fetchProductMasters = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/product_masters`);
      if (res.status === 200) {
        const transformed = res.data.data.map((item: any) => ({
          id: item.id?.toString(),
          customer: item.customer || "",
          no_of_variants: item.no_of_variants || 0,
          design: item.design || "",
          date: new Date(item.date).toLocaleDateString("en-GB"),
          mainCategory: item.main_category || "",
        }));
        setData(transformed);
      }
    } catch (err) {
      console.error("Failed to fetch product masters", err);
    }
  };

  useEffect(() => {
    fetchProductMasters();
  }, []);

  useEffect(() => {

    const fetchGroup = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/product_masters/groups`)
        if (response.status === 200 && response.data) {
          const group = response.data.data
          // Map fields to your form shape
          const transformed = group.map((group: any) => ({
            group_id: group.group_id,
            name: group.name,
            status: group.status === 1 ? "Enabled" : "Disabled"
          }))
          setGroupData(transformed)
        }
      } catch (err) {
        console.error("Failed to fetch group", err)
        navigate("/product-master")
      }
    }

    const fetchSubGroup = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/sub_groups`)
        if (response.status === 200 && response.data) {
          const group = response.data.data
          // Map fields to your form shape
          const transformed = group.map((group: any) => ({
            sub_group_id: group.sub_group_id,
            name: group.name,
            status: group.status === 1 ? "Enabled" : "Disabled"
          }))
          setSubGroupData(transformed)
        }
      } catch (err) {
        console.error("Failed to fetch sub group", err)
        navigate("/product-master")
      }
    }

    const fetchSubSubGroup = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/sub_sub_groups`)
        if (response.status === 200 && response.data) {
          const group = response.data.data
          // Map fields to your form shape
          const transformed = group.map((group: any) => ({
            sub_group_id: group.sub_group_id,
            name: group.name,
            status: group.status === 1 ? "Enabled" : "Disabled"
          }))
          setSubSubGroupData(transformed)
        }
      } catch (err) {
        console.error("Failed to fetch sub sub group", err)
        navigate("/product-master")
      }
    }

    const fetchShape = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/shape_size`)
        if (response.status === 200 && response.data) {
          const shapeSize = response.data.data
          // Map fields to your form shape
          const transformed = shapeSize.map((shapeSize: any) => ({
            shape_size_id: shapeSize.shape_size_id,
            shape: shapeSize.shape,
            status: shapeSize.status === 1 ? "Enabled" : "Disabled"
          }))
          setShapeSizeData(transformed)
        }
      } catch (err) {
        console.error("Failed to fetch Shape Size", err)
        navigate("/product-master")
      }
    }
  
    fetchGroup()
    fetchSubGroup()
    fetchSubSubGroup()
    fetchShape()
  }, [])

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;

    try {
      const res = await axios.delete(`${API_BASE_URL}/api/product_masters/${id}`);
      if (res.status === 200) {
        alert("Product Master deleted successfully.");
        fetchProductMasters();
      } else {
        alert("Failed to delete product master.");
      }
    } catch (error) {
      console.error("Delete error:", error);
      alert("Something went wrong.");
    }
  };

  const [productDetailData, setProductDetailData] = useState(null);

  const handleShow = async (id: string) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/product_masters/${id}`);
      if (res.status === 200) {
        const product = res.data.data;

        const transformedVariants = product.variants?.map(variant => ({
          id: variant.id,
          group: variant.group_name,
          subGroup: variant.sub_group,
          subSubGroup: variant.sub_sub_group,
          shape: variant.shape,
          size: variant.size,
          stones: variant.stones,
          weight: variant.weight,
          // These will be populated later
          availableSubGroups: [],
          availableSubSubGroups: []
        })) || [];
        
        setShowModalData(transformedVariants);

        setInitialData({
          design: product.design,
          date: product.date?.split("T")[0],
          customer: product.customer,
          mainCategory: product.main_category,
          subCategory: product.sub_category,
          collection: product.collection,
          cadDoneBy: product.cad_done_by,
          designedBy: product.designed_by,
          finding: product.finding,
          finish: product.finish,
          rhodium: product.rhodium,
          defaultImageTick: product.default_image_tick,
          main_image: product.main_image || "",
          catalog_image: product.catalog_image || "",
          cad_image: product.cad_image || "",
          design_image: product.design_image || "",
          refrence_image: product.refrence_image || "",
          cad_file: product.cad_file || "",
          cad_stl: product.cad_stl || "",
          variants: transformedVariants
        });
      } else {
        alert("Failed to fetch product details.");
      }
    } catch (error) {
      console.error("Fetch error:", error);
      alert("Something went wrong.");
    }
  };

  const handleAddProductMaster = () => {
    navigate("/product-master/add");
  };

  const columns = ProductMasterColumns({
    onDelete: handleDelete,
    onShow: handleShow,
  });


  const handleGroupChange = async (groupId: string) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/product_masters/sub_groups/${groupId}`);
      if (response.status === 200) {
        return response.data.data.map((item: any) => ({
          sub_group_id: item.sub_group_id,
          name: item.name,
          status: item.status === 1 ? "Enabled" : "Disabled"
        }));
      }
    } catch (error) {
      console.error("Failed to fetch sub groups:", error);
      return [];
    }
  };

  const handleSubGroupChange = async (subGroupId: string) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/product_masters/sub_sub_groups/${subGroupId}`);
      if (response.status === 200) {
        return response.data.data.map((item: any) => ({
          sub_sub_group_id: item.sub_sub_group_id,
          name: item.name,
          status: item.status === 1 ? "Enabled" : "Disabled"
        }));
        // setSubSubGroupData(subSubGroups); // Clear sub-sub-group for now
      }
    } catch (error) {
      console.error("Failed to fetch sub sub groups:", error);
      return [];
    }
  };

  return (
    <div className="p-5">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-semibold">Product Master Management</h1>
        <Button onClick={handleAddProductMaster}>+ Add Product Master</Button>
      </div>

      <ProductMasterDataTable columns={columns} data={data} />

      {editProductMasterData && (
        <EditProductMasterForm
          user={editProductMasterData}
          onCancel={() => setEditProductMasterData(null)}
          onSave={() => {
            setEditProductMasterData(null);
            fetchProductMasters();
          }}
        />
      )}

      {showModalData && (
        <ProductDetailModal
          data={showModalData}
          initialValues={initialData}
          groupValues={groupData} // pass groupValues fetched earlier
          shapeSizeValues={shapeSizeData} // if available
          fetchSubGroups={handleGroupChange}  // Renamed to be more descriptive
          fetchSubSubGroups={handleSubGroupChange} 
          onClose={() => setShowModalData(null)}
          onSave={() => setShowModalData(null)}
        />
      )}
    </div>
  );
}